import { WarningOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Descriptions, Drawer, Flex, Form, Input, InputNumber, Row, Select, Skeleton, Statistic, Table, Tag, Typography } from "antd";
import { Link } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { useWarehouses } from "@/lib/useWarehouses";
import { rupiahInput } from "../procurement/common";

type CostRow = Schemas["CostRow"];
type CostDetail = Schemas["CostDetail"];
type History = CostDetail["history"][number];
const STATUS_CHOICES = ["all", "missing", "set"] as const;

/** Keuangan > HPP & Nilai Persediaan (Plan §6.19). */
export function Costs() {
  const { t, formatMoney, formatNumber, formatDate } = useI18n();
  const warehouses = useWarehouses();
  const status = useStatusFilter(STATUS_CHOICES, "all");
  const search = useSearchFilter();
  const page = useListPage();
  const [warehouseId, setWarehouseId] = useUrlFilter<string>("warehouseId", "");
  const [openSku, setOpenSku] = useUrlFilter<string>("sku", "");

  const value = useQuery({
    queryKey: ["finance", "inventory-value"],
    queryFn: () => unwrap(api.GET("/v1/finance/inventory-value")),
  });
  const filters = { ...(status.value !== "all" ? { status: status.value } : {}), ...(warehouseId ? { warehouseId } : {}), ...search.query };
  const list = useQuery({
    queryKey: ["finance", "costs", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/finance/costs", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  const v = value.data;
  const multiWarehouse = warehouses.active.length > 1;

  return (
    <>
      <Typography.Title level={2} style={{ margin: "0 0 16px", fontSize: 28 }}>
        {t("cost.title")}
      </Typography.Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small" loading={value.isPending}>
            <Statistic title={t("cost.totalValue")} value={v ? formatMoney(v.value) : "—"} />
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {t("cost.totalUnits", { n: formatNumber(v?.units ?? 0) })}
            </Typography.Text>
            {v && v.inTransit.orders > 0 && (
              <Typography.Paragraph type="secondary" style={{ fontSize: 13, margin: "4px 0 0" }}>
                {t("cost.inTransit", { n: formatNumber(v.inTransit.orders), units: formatNumber(v.inTransit.units), value: formatMoney(v.inTransit.value) })}
              </Typography.Paragraph>
            )}
            {v && v.damagedUnits > 0 && (
              <Typography.Paragraph type="warning" style={{ fontSize: 13, margin: "4px 0 0" }}>
                {t("cost.damaged", { n: formatNumber(v.damagedUnits), value: formatMoney(v.damagedValue) })}{" "}
                <Link to="/app/warehouse/damaged">{t("cost.damagedLink")}</Link>
              </Typography.Paragraph>
            )}
          </Card>
        </Col>
        {multiWarehouse && (
          <Col xs={24} md={16}>
            <Card size="small" loading={value.isPending}>
              <Table
                size="small"
                rowKey="warehouseId"
                pagination={false}
                dataSource={v?.warehouses}
                onRow={(w) => ({ onClick: () => setWarehouseId(w.warehouseId), style: { cursor: "pointer" } })}
                columns={[
                  { title: t("cost.warehouse"), dataIndex: "warehouseName" },
                  { title: t("cost.units"), dataIndex: "units", width: 110, align: "right", render: (n: number) => <span className="z-num">{formatNumber(n)}</span> },
                  { title: t("cost.value"), dataIndex: "value", width: 170, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
                ]}
              />
            </Card>
          </Col>
        )}
      </Row>
      {v && v.unitsWithoutCost > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("cost.missingAlert", { n: formatNumber(v.unitsWithoutCost) })}
          action={
            status.value !== "missing" && (
              <Button size="small" onClick={() => status.set("missing")}>
                {t("cost.showMissing")}
              </Button>
            )
          }
        />
      )}
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("cost.search")} />
        <StatusFilter
          filter={status}
          options={[
            { value: "all", label: t("cost.filter.all") },
            { value: "missing", label: t("cost.filter.missing") },
            { value: "set", label: t("cost.filter.set") },
          ]}
        />
        {multiWarehouse && (
          <Select
            value={warehouseId}
            onChange={setWarehouseId}
            style={{ width: 220 }}
            options={[{ value: "", label: t("inventory.allWarehouses") }, ...warehouses.active.map((w) => ({ value: w.id, label: w.name }))]}
          />
        )}
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/finance/costs/export"
          query={filters}
          filename={t("cost.title")}
          columns={[
            { key: "code", label: t("catalog.code") },
            { key: "name", label: t("cost.name") },
            { key: "onHand", label: t("cost.units"), kind: "number" },
            { key: "unitCost", label: t("cost.unitCost"), kind: "number" },
            { key: "value", label: t("cost.value"), kind: "number" },
            { key: "costUpdatedAt", label: t("cost.updatedAt"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<CostRow>
        rowKey="skuId"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("cost.empty") }}
        onRow={(r) => ({ onClick: () => setOpenSku(r.skuId), style: { cursor: "pointer" } })}
        columns={[
          {
            title: t("catalog.code"),
            render: (_, r) => (
              <span>
                <span className="z-num">{r.code}</span>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.name}
                </Typography.Text>
              </span>
            ),
          },
          { title: t("cost.units"), dataIndex: "onHand", width: 110, align: "right", render: (n: number) => <span className="z-num">{formatNumber(n)}</span> },
          {
            title: t("cost.unitCost"),
            dataIndex: "unitCost",
            width: 160,
            align: "right",
            render: (c: number | null, r) =>
              c == null ? (
                r.onHand > 0 ? (
                  <Tag icon={<WarningOutlined />} color="warning">
                    {t("cost.missing")}
                  </Tag>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                )
              ) : (
                <span className="z-num">{formatMoney(c)}</span>
              ),
          },
          { title: t("cost.value"), dataIndex: "value", width: 170, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
          { title: t("cost.updatedAt"), dataIndex: "costUpdatedAt", width: 160, render: (d: string | null) => (d ? formatDate(d) : "—") },
        ]}
      />
      <CostDrawer skuId={openSku || null} onClose={() => setOpenSku("")} />
    </>
  );
}

function CostDrawer({ skuId, onClose }: { skuId: string | null; onClose: () => void }) {
  const { t, formatMoney, formatNumber, formatDateTime } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ unitCost: number; note?: string }>();
  const detail = useQuery({
    queryKey: ["finance", "costs", "detail", skuId],
    queryFn: () => unwrap(api.GET("/v1/finance/costs/{skuID}", { params: { path: { skuID: skuId! } } })),
    enabled: !!skuId,
  });
  const save = useMutation({
    mutationFn: (v: { unitCost: number; note?: string }) =>
      unwrap(api.PUT("/v1/finance/costs/{skuID}", { params: { path: { skuID: skuId! } }, body: { unitCost: v.unitCost, note: v.note ?? "" } })),
    onSuccess: () => {
      void message.success(t("cost.saved"));
      form.resetFields();
      void queryClient.invalidateQueries({ queryKey: ["finance"] });
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  const d = detail.data;

  const source = (h: History) => {
    if (h.kind === "manual") return h.note ? `${t("cost.kind.manual")} · ${h.note}` : t("cost.kind.manual");
    if (h.refType === "purchase_order" && h.refId && can("procurement.view")) return <Link to={`/app/procurement/orders/${h.refId}`}>{h.refNumber}</Link>;
    if (h.refType === "inbound_receipt" && h.refId && can("warehouse.operate")) return <Link to={`/app/warehouse/inbound/${h.refId}`}>{h.refNumber}</Link>;
    return h.refNumber;
  };

  return (
    <Drawer open={skuId !== null} onClose={onClose} size="large" title={d ? `${d.code} · ${d.name}` : t("cost.title")} destroyOnHidden>
      {!d ? (
        <Skeleton active />
      ) : (
        <Flex vertical gap={20}>
          <Descriptions
            column={{ xs: 1, sm: 3 }}
            size="small"
            items={[
              { key: "cost", label: t("cost.unitCost"), children: d.unitCost == null ? t("cost.missing") : <span className="z-num">{formatMoney(d.unitCost)}</span> },
              { key: "units", label: t("cost.units"), children: <span className="z-num">{formatNumber(d.onHand)}</span> },
              { key: "value", label: t("cost.value"), children: <span className="z-num">{formatMoney(d.value)}</span> },
            ]}
          />
          {d.stock.length > 1 && (
            <Table
              size="small"
              rowKey="warehouseId"
              pagination={false}
              dataSource={d.stock}
              columns={[
                { title: t("cost.warehouse"), dataIndex: "warehouseName" },
                { title: t("cost.units"), dataIndex: "onHand", width: 110, align: "right", render: (n: number) => <span className="z-num">{formatNumber(n)}</span> },
                { title: t("cost.value"), dataIndex: "value", width: 170, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
              ]}
            />
          )}
          {can("finance.manage") && (
            <Card size="small" title={d.unitCost == null ? t("cost.setOpening") : t("cost.correct")}>
              <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
                {t("cost.setHelp")}
              </Typography.Paragraph>
              <Form form={form} layout="inline" requiredMark={false} onFinish={(v) => save.mutate(v)} style={{ rowGap: 8 }}>
                <Form.Item name="unitCost" rules={[{ required: true, message: t("field.required") }]}>
                  <InputNumber<number> min={0} max={1_000_000_000_000} precision={0} prefix="Rp" placeholder={t("cost.unitCost")} style={{ width: 180 }} {...rupiahInput} />
                </Form.Item>
                <Form.Item name="note" style={{ flex: 1, minWidth: 180 }}>
                  <Input maxLength={500} placeholder={t("cost.note")} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={save.isPending}>
                  {t("common.save")}
                </Button>
              </Form>
            </Card>
          )}
          <div>
            <Typography.Title level={5}>{t("cost.history")}</Typography.Title>
            <Table<History>
              size="small"
              rowKey="id"
              dataSource={d.history}
              pagination={d.history.length > 20 ? { pageSize: 20 } : false}
              locale={{ emptyText: t("cost.historyEmpty") }}
              columns={[
                { title: t("cost.when"), dataIndex: "at", width: 140, render: (v: string) => formatDateTime(v) },
                { title: t("cost.source"), render: (_, h) => source(h) },
                {
                  title: t("cost.in"),
                  width: 140,
                  align: "right",
                  render: (_, h) =>
                    h.kind === "receipt" ? (
                      <span className="z-num">
                        {formatNumber(h.quantity)} × {formatMoney(h.unitCost)}
                      </span>
                    ) : (
                      "—"
                    ),
                },
                {
                  title: t("cost.change"),
                  width: 170,
                  align: "right",
                  render: (_, h) => (
                    <span className="z-num">
                      {h.avgBefore == null ? "—" : formatMoney(h.avgBefore)} → <strong>{formatMoney(h.avgAfter)}</strong>
                    </span>
                  ),
                },
                { title: t("cost.by"), dataIndex: "actorName", width: 110 },
              ]}
            />
          </div>
        </Flex>
      )}
    </Drawer>
  );
}
